import React, { useState } from "react";
import { Plus, ChevronRight } from "lucide-react";
import loader from "../../../../../components/recharts/loader.gif";
import { AddCapacityModal, CapacityRow } from "../../PowerProcurement/AddOffer";

interface CapacityOffer {
  start_date: string;
  end_date: string;
  source_type: string;
  id: number;
  source_id: number;
  plant_capacity: string;
  plf: string;
  variable_charge: string;
  fixed_charge: string;
  status?: boolean;
  offerType: "capacity";
}

interface CapacityConsoleProps {
  capacities: CapacityOffer[];
  loading: boolean;
  error: string | null;
  isSliderOpen: boolean;
  onAddCapacity: (data: CapacityRow[]) => Promise<void>;
  onToggleOffer: (id: number) => void;
  isReadOnly?: boolean;
}

export function CapacityConsole({
  capacities,
  loading,
  error,
  isSliderOpen,
  onAddCapacity,
  onToggleOffer,
  isReadOnly = false,
}: CapacityConsoleProps) {
  const [isAddCapacityModalOpen, setIsAddCapacityModalOpen] = useState(false);
  const [capacityScrollIndex, setCapacityScrollIndex] = useState(0);
  const [modalLoading, setModalLoading] = useState(false);

  const handleAddCapacity = () => {
    setIsAddCapacityModalOpen(true);
  };

  const handleAddCapacitySubmit = async (data: CapacityRow[]) => {
    setModalLoading(true);
    try {
      await onAddCapacity(data);
      setIsAddCapacityModalOpen(false);
    } catch (err) {
      console.error("Error adding capacity:", err);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCapacityScroll = (direction: "up" | "down") => {
    const activeCapacities = capacities.filter((c) => c.status);
    if (direction === "down" && capacityScrollIndex < activeCapacities.length - 1) {
      setCapacityScrollIndex(capacityScrollIndex + 1);
    } else if (direction === "up" && capacityScrollIndex > 0) {
      setCapacityScrollIndex(capacityScrollIndex - 1);
    }
  };

  // Active offers for condensed view
  const activeCapacities = capacities.filter((c) => c.status);
  const visibleCapacities = activeCapacities.slice(capacityScrollIndex, capacityScrollIndex + 1);

  // Full View (when slider is open)
  return (
    <>
      <AddCapacityModal
        isModalOpen={isAddCapacityModalOpen}
        setIsModalOpen={setIsAddCapacityModalOpen}
        modalTitle="Add Capacity"
        onSubmit={handleAddCapacitySubmit}
        loading={modalLoading}
        setLoading={setModalLoading}
      />

      <div className="bg-white rounded-xl border border-[#F1935C] p-1.5 mb-2">
        <div className="flex justify-between items-center pb-2 border-b border-[#FBECE4] mb-2">
          <h1 className="text-sm font-medium text-[#9AA0A6]">
            Generation Capacity
          </h1>
          <button
            onClick={handleAddCapacity}
            disabled={isReadOnly}
            className={`w-6 h-6 flex items-center justify-center rounded border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isReadOnly
              ? "bg-gray-100 text-gray-400 border-gray-200"
              : "border-[#F1935C] text-[#F1935C] hover:bg-[#F1935C] hover:text-white"
              }`}
          >
            <Plus strokeWidth={2.5} className="w-4 h-4" />
          </button>
        </div>

        <div className="!overflow-auto !max-h-[15.2vh] custom-scroll pr-1">
          {loading && capacities.length === 0 ? (
            <div className="h-full w-full bg-white flex items-center justify-center">
              <img
                src={loader}
                width={200}
                alt="loader"
                className="block mt-[2rem]"
              />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">{error}</div>
          ) : capacities.length === 0 ? (
            <div className="text-gray-600 text-center py-8 border border-dashed border-[#F1935C] rounded-lg bg-[#FFFAF6]">
              <h2 className="mb-1 text-sm text-gray-600">No capacities added yet</h2>
              <p className="text-xs text-gray-400">
                Click "+" to get started
              </p>
            </div>
          ) : (
            capacities.map((capacity) => (
              <div
                key={`capacity-${capacity.id}`}
                className="border border-[#FBECE4] rounded-lg p-1.5 bg-white mb-1.5 relative mt-2"
              >
                <div className="absolute top-[-13px] left-2 bg-white px-1">
                  <span className="text-[10px] font-bold text-[#F1935C]">
                    {capacity.source_type || "N/A"}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-y-1 gap-x-1 mt-0.5">
                  <div>
                    <label className="block text-[9px] text-[#9AA0A6]">
                      Start Date
                    </label>
                    <div className="text-[10px] font-medium text-[#34656D]">
                      {capacity.start_date || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] text-[#9AA0A6]">
                      End Date
                    </label>
                    <div className="text-[10px] font-medium text-[#34656D]">
                      {capacity.end_date || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] text-[#9AA0A6]">
                      PLF
                    </label>
                    <div className="text-[10px] font-medium text-[#34656D]">
                      {capacity.plf || "N/A"}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[9px] text-[#9AA0A6]">
                      Fixed Charge
                    </label>
                    <div className="text-[10px] font-medium text-[#34656D]">
                      {capacity.fixed_charge ? `${capacity.fixed_charge}` : "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] text-[#9AA0A6]">
                      VC
                    </label>
                    <div className="text-[10px] font-medium text-[#34656D]">
                      {capacity.variable_charge
                        ? `${capacity.variable_charge}`
                        : "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-[9px] text-[#9AA0A6]">
                      Quantum
                    </label>
                    <div className="text-[10px] font-medium text-[#34656D]">
                      {capacity.plant_capacity
                        ? `${capacity.plant_capacity} MW`
                        : "N/A"}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}